import { useEffect, useState } from 'react';
import { useToast } from '../../../components/ToastContext';

const emptyForm = {
  name: '',
  charge_type: 'fee',
  calc_type: 'percent',
  value: '',
  applies_to: 'base',
  per_night: false,
  is_included_in_price: false,
  priority: 1,
  is_active: true,
};

const TaxesFeesTab = ({ accessToken, propertyId, listFn, saveFn, deleteFn }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();

  const load = async () => {
    if (!propertyId) return;
    setError('');
    try {
      const rows = await listFn(accessToken, propertyId);
      setItems(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load taxes/fees');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!propertyId) {
      toast.error('Select a property first');
      return;
    }
    try {
      const payload = { ...form, property_id: propertyId };
      if (!editingId) delete payload.id;
      const saved = await saveFn(accessToken, editingId ? { ...payload, id: editingId } : payload);
      setItems((prev) => {
        const filtered = prev.filter((i) => i.id !== saved.id);
        return [saved, ...filtered];
      });
      setForm(emptyForm);
      setEditingId(null);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...emptyForm,
      ...row,
    });
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this tax/fee?')) return;
    await deleteFn(accessToken, row.id);
    setItems((prev) => prev.filter((i) => i.id !== row.id));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <p className="text-lg font-semibold text-slate-900">Taxes & Fees</p>
          <p className="text-sm text-slate-600">Cleaning, service fee, GST or platform fees.</p>
        </div>
        <button className="text-xs text-emerald-700 underline" onClick={load}>
          Refresh
        </button>
      </div>
      {error && <div className="px-4 py-2 text-sm text-red-600">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-slate-900">{editingId ? 'Edit charge' : 'Add tax/fee'}</p>
              <p className="text-xs text-slate-500">Percent or fixed, with optional inclusion flag.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
          </div>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="Cleaning fee / GST"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Charge type</span>
              <select
                value={form.charge_type}
                onChange={(e) => setForm((f) => ({ ...f, charge_type: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="fee">Fee</option>
                <option value="tax">Tax</option>
              </select>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Calc type</span>
              <select
                value={form.calc_type}
                onChange={(e) => setForm((f) => ({ ...f, calc_type: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="percent">Percent</option>
                <option value="fixed">Fixed</option>
                <option value="per_night">Per night</option>
                <option value="per_guest">Per guest</option>
                <option value="per_booking">Per booking</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Value</span>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Priority</span>
              <input
                type="number"
                min="1"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.per_night}
                onChange={(e) => setForm((f) => ({ ...f, per_night: e.target.checked }))}
              />
              Per night (for fixed/fee)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.is_included_in_price}
                onChange={(e) => setForm((f) => ({ ...f, is_included_in_price: e.target.checked }))}
              />
              Included in displayed price
            </label>
          </div>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Applies to</span>
            <select
              value={form.applies_to}
              onChange={(e) => setForm((f) => ({ ...f, applies_to: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            >
              <option value="base">Base</option>
              <option value="base_plus_fees">Base + fees</option>
              <option value="total">Total</option>
            </select>
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              {editingId ? 'Update charge' : 'Create charge'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="text-sm text-slate-600 underline"
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <p className="text-lg font-semibold text-slate-900">Charges</p>
              <p className="text-xs text-slate-500">Visible at checkout and applied to pricing.</p>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {items.length === 0 && <div className="p-4 text-sm text-slate-600">No taxes/fees yet.</div>}
            {items.map((t) => (
              <div key={t.id} className="flex items-start justify-between px-4 py-3 hover:bg-slate-50">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{t.name}</p>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{t.charge_type}</span>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                      {t.calc_type} {t.value}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Applies to {t.applies_to}</p>
                  {t.per_night && <p className="text-xs text-slate-500">Per night</p>}
                  {t.is_included_in_price && <p className="text-xs text-emerald-600">Included in price</p>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1 text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!t.is_active}
                      onChange={(e) => saveFn(accessToken, { ...t, is_active: e.target.checked }).then(load)}
                    />
                    Active
                  </label>
                  <button className="text-emerald-700 underline" onClick={() => startEdit(t)}>
                    Edit
                  </button>
                  <button className="text-red-600 underline" onClick={() => remove(t)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxesFeesTab;
